/**
 * Turn a pasted video link into one that can actually be embedded.
 *
 * People paste the URL from their browser's address bar — a YouTube *watch*
 * link, or a youtu.be share link. Neither can be put in an iframe: YouTube
 * sends X-Frame-Options and the browser refuses to render it, leaving a blank
 * grey box that looks like a broken image. Only the /embed/ form works.
 *
 * So rather than asking editors to know that, we convert it here. Anything we
 * cannot confidently convert returns null and the caller shows a plain link
 * instead of an empty frame.
 */

/** Extract the 11-character YouTube video id from any of its URL shapes. */
function youTubeId(url) {
  const patterns = [
    /(?:youtube\.com\/watch\?(?:.*&)?v=)([\w-]{11})/i,
    /(?:youtu\.be\/)([\w-]{11})/i,
    /(?:youtube\.com\/embed\/)([\w-]{11})/i,
    /(?:youtube\.com\/shorts\/)([\w-]{11})/i,
    /(?:youtube\.com\/live\/)([\w-]{11})/i,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
}

function vimeoId(url) {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  return match ? match[1] : null;
}

/**
 * @returns {{ type: string, src: string } | null} an embeddable source, or null
 *          if the link should be offered as a plain link instead.
 */
export function toEmbed(rawUrl) {
  const url = String(rawUrl || "").trim();
  if (!url) return null;

  const yt = youTubeId(url);
  if (yt) {
    // youtube-nocookie avoids setting tracking cookies until the visitor plays.
    return { type: "youtube", src: `https://www.youtube-nocookie.com/embed/${yt}` };
  }

  const vimeo = vimeoId(url);
  if (vimeo) return { type: "vimeo", src: `https://player.vimeo.com/video/${vimeo}` };

  // A direct video file can be played natively.
  if (/\.(mp4|webm|ogg)(\?|$)/i.test(url)) return { type: "file", src: url };

  return null;
}

export default toEmbed;
